import { baseApi } from '@/shared/lib/baseApi';

/**
 * Response returned after verifying email using activation token
 * Confirms whether the activation link is valid.
 */
interface VerifyEsimEmailResponse {
  email: string;
  verified: boolean;
  activationSignature: string;
  expiresAt: string;
}

/**
 * Response returned after generating the eSIM QR code
 * Includes base64 QR image and related metadata
 */
export interface GenerateEsimQrResponse {
  email: string;
  method: string;
  iccid: string;
  qrCode: string;
  activationCode?: string;
  transactionId?: string;
  esimId?: string;
  customerEmail?: string;
  expiresAt?: string;
  iosDeeplink?: string;
  androidDeeplink?: string;
}

/**
 * Generic API response wrapper used by backend
 * Actual payload is nested inside `data`
 */
interface ApiResponse<T> {
  statusCode?: number;
  success?: boolean;
  message: string;
  data: T;
}

/**
 * Detects device operating system from browser user agent
 */
const getDeviceOS = (): string => {
  const ua = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/win/.test(ua)) return 'windows';
  if (/mac/.test(ua)) return 'macos';
  if (/linux/.test(ua)) return 'linux';

  return 'unknown';
};

/**
 * Determines request source (web vs app)
 */
const getInitiatedFrom = (): string => {
  if (typeof window !== 'undefined') {
    return 'web';
  }
  return 'unknown';
};

/**
 * eSIM QR related API endpoints
 */
export const esimQrApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Verifies email activation token received via email link
     */
    verifyEsimEmail: builder.mutation<
      VerifyEsimEmailResponse,
      { token: string }
    >({
      query: (body) => ({
        url: '/api/enterprises/verify-email',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<VerifyEsimEmailResponse>) =>
        response.data,
    }),

    /**
     * Generates eSIM activation QR code
     * Requires verified email and activation signature
     */
    generateEsimQr: builder.mutation<
      GenerateEsimQrResponse,
      { email: string; signature: string }
    >({
      query: ({ email, signature }) => ({
        url: '/api/sim_profile/esim/send-activation',
        method: 'POST',
        body: {
          email,
          signature,
          device_os: getDeviceOS(),
          initiated_from: getInitiatedFrom(),
        },
      }),
      transformResponse: (response: ApiResponse<GenerateEsimQrResponse>) =>
        response.data,
    }),
  }),
});

/**
 * Auto-generated RTK Query hooks
 */
export const { useVerifyEsimEmailMutation, useGenerateEsimQrMutation } =
  esimQrApi;
